import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams, Outlet } from 'react-router-dom';
import UserAccountModal from './UserAccountModal';
import { useAuth } from '../../context/AuthContext';
import UserAvatar from '../../components/common/UserAvatar';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { ADMIN_ENABLED } from '../../utils/adminFeature';
import { useExam } from '../../context/ExamContext';

const getAdminService = () => import('../../services/adminService').then(m => m.default);
import { useQuiz } from '../../context/QuizContext';
import { useHelp } from '../../context/HelpContext';
import { useModules } from '../../context/LevelsContext';
import useDashboardPrefs from '../../hooks/dashboard/useDashboardPrefs';
import ExamSession from '../exams/ExamSession';
import QuizSession from '../courses/QuizSession';
import LevelSelectorModal from './modals/LevelSelectorModal';
import HelpSpotlight from '../../components/help/HelpSpotlight';
import HelpTrigger from '../../components/help/HelpTrigger';

const DashboardLayout = () => {
    const { user, logout, isAdmin, previewMode, setPreviewMode } = useAuth();
    const { language, toggleLanguage } = useLanguage();
    const { theme, setTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { levelId } = useParams();
    const isDashboard = location.pathname.startsWith('/dashboard');
    const { examId } = useExam();
    const { quizId } = useQuiz();
    const { startTour, hasSeenPage, helpPrefs } = useHelp();
    const { modules } = useModules();

    const { prefs, setLastLevel, setSidebarOrder, setAddedLevels, setHiddenLevels, flushPrefs } = useDashboardPrefs(user?.dashboardPrefs);
    const isSchoolAdmin = (user?.nroEcole ?? 0) > 0;

    // Admin: impersonation
    const [impersonating,    setImpersonating]    = useState(null);
    const [impSearchQuery,   setImpSearchQuery]   = useState('');
    const [impSearchResults, setImpSearchResults] = useState([]);
    const [impSearching,     setImpSearching]     = useState(false);
    const impSearchTimer = useRef(null);

    const [showLevelModal,   setShowLevelModal]   = useState(false);
    const [showAccountModal, setShowAccountModal] = useState(false);
    const [accountSection,   setAccountSection]   = useState('infos');
    const [showFloatingMenu, setShowFloatingMenu] = useState(false);
    const floatingMenuRef  = useRef(null);

    // URL → modal section mapping for /dashboard/account/* routes
    const URL_TO_SECTION = {
        '': 'infos', orders: 'orders', victories: 'victories', infos: 'infos',
        parrainages: 'parrainages', notifications: 'notifications',
        recommendation: 'recommendation', 'hours-record': 'hours',
        'school-stats': 'school-stats', help: 'aide', messages: 'notifications',
    };

    // Auto-open account modal when navigating to /dashboard/account/* — EXCEPT for
    // content pages (blog/faq/about/privacy) which render full-page via the Outlet.
    useEffect(() => {
        const match = location.pathname.match(/^\/dashboard\/account(?:\/(.*))?$/);
        if (match) {
            const segment = (match[1] || '').split('/')[0];
            const isContentRoute = ['blog', 'faq', 'about', 'privacy'].includes(segment);
            if (isContentRoute) {
                setShowAccountModal(false);
                return;
            }
            const section = URL_TO_SECTION[segment] ?? 'infos';
            setAccountSection(section);
            setShowAccountModal(true);
        } else {
            setShowAccountModal(false);
        }
    }, [location.pathname]);

    const closeAccountModal = () => {
        setShowAccountModal(false);
        // Navigate away from account route so the modal doesn't reopen
        if (location.pathname.startsWith('/dashboard/account')) {
            navigate('/dashboard', { replace: true });
        }
    };

    // Close floating popup on outside click
    useEffect(() => {
        if (!showFloatingMenu) return;
        const handler = (e) => {
            if (floatingMenuRef.current && !floatingMenuRef.current.contains(e.target)) {
                setShowFloatingMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showFloatingMenu]);

    // Admin: load impersonation status
    useEffect(() => {
        if (!ADMIN_ENABLED || !isAdmin) return;
        getAdminService().then(svc =>
            svc.impersonateStatus()
                .then(r => setImpersonating(r.impersonating || null))
                .catch(() => {})
        );
    }, [isAdmin]);

    const handleImpSearch = (q) => {
        setImpSearchQuery(q);
        if (impSearchTimer.current) clearTimeout(impSearchTimer.current);
        // #id syntax — direct ID lookup, no dropdown needed
        if (q.startsWith('#')) {
            setImpSearchResults([]);
            return;
        }
        if (q.length < 2) { setImpSearchResults([]); return; }
        setImpSearching(true);
        impSearchTimer.current = setTimeout(async () => {
            try {
                const svc = await getAdminService();
                const results = await svc.userSearch(q);
                setImpSearchResults(results?.users || []);
            } catch { setImpSearchResults([]); }
            setImpSearching(false);
        }, 300);
    };

    const startImpersonation = async (userId) => {
        try {
            const svc = await getAdminService();
            await svc.impersonateStart(userId);
            window.location.reload();
        } catch (e) { console.error('Start impersonation failed', e); }
    };

    const stopImpersonation = async () => {
        try {
            const svc = await getAdminService();
            await svc.impersonateStop();
            window.location.reload();
        } catch (e) { console.error('Stop impersonation failed', e); }
    };

    // Note: sidebar help steps are now included in 'level-overview' tour

    const handleLogout = async () => {
        // Flush le sync debounced de useDashboardPrefs AVANT que logout() clear la session,
        // sinon une modif récente (ex. setLastLevel('ppl') < 1s avant le clic Déconnexion)
        // arriverait au serveur en 401 → user revient sur l'ancien lastLevel à la reconnexion.
        await flushPrefs();
        logout();
        navigate('/');
    };

    const handleLevelClick = (moduleId) => {
        navigate(`/dashboard/${moduleId}`);
        setLastLevel(moduleId);
        window.scrollTo(0, 0);
    };

    // Compute visible + sorted levels (shared across desktop, mobile pills, mobile bottom nav)
    // Rule: (PSTAR | active subscriptions | manually added) AND not explicitly hidden
    // Packs are never shown in sidebar (they are purchase bundles)
    const visibleLevels = useMemo(() => {
        const activeLevelsArray = Array.isArray(user?.activeLevels)
            ? user.activeLevels
            : Object.values(user?.activeLevels || {});

        const addedLevels = prefs.addedLevels || [];
        const hiddenLevels = prefs.hiddenLevels || [];
        const sidebarOrder = prefs.sidebarOrder || [];

        const isActive = (moduleId) => activeLevelsArray.some(level =>
            String(level.stageId || level.nroNiveau || '').toLowerCase().includes(moduleId) ||
            (level.stageId === 'pstar' && moduleId === 'pstar') ||
            (level.stageId === 'ppl' && moduleId === 'ppl') ||
            (level.nroNiveau === '1' && moduleId === 'pstar') ||
            (level.nroNiveau === '2' && moduleId === 'ppl') ||
            (level.packVar && String(level.packVar).toLowerCase().includes(moduleId))
        );

        const filtered = modules.filter(module => {
            if (module.isPack) return false;
            if (hiddenLevels.includes(module.id)) return false;
            if (['pstar', 'ppl', 'croa'].includes(module.id)) return true;
            if (isActive(module.id)) return true;
            if (addedLevels.includes(module.id)) return true;
            return false;
        });

        // Sort by user's sidebarOrder preference
        if (sidebarOrder.length > 0) {
            filtered.sort((a, b) => {
                const idxA = sidebarOrder.indexOf(a.id);
                const idxB = sidebarOrder.indexOf(b.id);
                const posA = idxA >= 0 ? idxA : sidebarOrder.length + modules.indexOf(a);
                const posB = idxB >= 0 ? idxB : sidebarOrder.length + modules.indexOf(b);
                return posA - posB;
            });
        }

        return filtered;
    }, [modules, user?.activeLevels, prefs.addedLevels, prefs.hiddenLevels, prefs.sidebarOrder]);

    // Content pages (blog, faq, about, privacy) hide the level sidebar.
    // Match both legacy /dashboard/blog (redirected) and current /dashboard/account/blog paths.
    const isContentPage = /^\/dashboard\/(?:account\/)?(blog|faq|about|privacy)/.test(location.pathname);

    return (
        <div className="flex h-screen bg-gray-50 dark:bg-black pt-16 lg:pt-0 transition-colors duration-300 overflow-x-hidden">
            {/* Sidebar - Desktop (hidden on content pages) */}
            {!isContentPage && (
            <div className="w-24 lg:w-32 bg-white dark:bg-[#0a0a0a] border-r border-gray-200 dark:border-white/5 hidden lg:flex flex-col py-6 shadow-sm dark:shadow-none">
                {/* Logo */}
                <div className="flex items-center justify-center px-3 mb-6 flex-shrink-0">
                    <img src="/img/logo-nizus.svg" alt="Nizus" className="h-5 w-auto invert dark:invert-0" />
                </div>

                {/* Level Pills — contained card */}
                <div className="flex-1 mx-2 mb-2 rounded-2xl bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5 overflow-hidden flex flex-col min-h-0">
                    <nav data-help="sidebar-levels" className="flex-1 px-2 py-3 space-y-2.5 overflow-y-auto custom-scrollbar flex flex-col items-center">
                        {visibleLevels.map((module) => {
                            const isActive = levelId === module.id;
                            return (
                                <button
                                    key={module.id}
                                    onClick={() => handleLevelClick(module.id)}
                                    className="w-full max-w-[80px] rounded-2xl py-2.5 px-2 text-[10px] font-black text-center leading-tight transition-all relative"
                                    style={{
                                        backgroundColor: isActive ? module.newBackgroundColor : 'transparent',
                                        color: isActive ? '#fff' : 'rgba(130,130,130,0.9)',
                                        border: `1.5px solid ${isActive ? module.newBackgroundColor : (theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db')}`,
                                    }}
                                    onMouseEnter={e => {
                                        if (!isActive) {
                                            e.currentTarget.style.backgroundColor = `${module.newBackgroundColor}F0`;
                                            e.currentTarget.style.borderColor = module.newBackgroundColor;
                                            e.currentTarget.style.color = '#fff';
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        if (!isActive) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.borderColor = theme === 'dark' ? 'rgba(255,255,255,0.15)' : '#d1d5db';
                                            e.currentTarget.style.color = 'rgba(130,130,130,0.9)';
                                        }
                                    }}
                                >
                                    {module.titleObj?.[language] || module.title}
                                </button>
                            );
                        })}

                        {/* Add level button */}
                        <button
                            data-help="add-level-button"
                            onClick={() => setShowLevelModal(true)}
                            className="mt-1 w-10 h-10 rounded-2xl border border-dashed border-gray-300 dark:border-white/10 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-white hover:border-blue-300 dark:hover:border-white/20 flex items-center justify-center text-xl font-light transition-all hover:scale-[1.05] active:scale-[0.95]"
                        >
                            +
                        </button>
                    </nav>
                </div>

                {/* Bottom controls — inside sidebar flow */}
                <div className="flex-shrink-0 flex flex-col items-center gap-2 pb-4 pt-2">
                    {/* Avatar + popup */}
                    <div
                        ref={floatingMenuRef}
                        className="relative"
                    >
                        {showFloatingMenu && (
                            <div className="absolute left-14 bottom-0 bg-white dark:bg-[#1c1c1c] border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden w-48 py-1 z-50">
                                {isAdmin && (
                                    <>
                                        <div className="px-3 pt-2 pb-1 flex items-center gap-2">
                                            <button onClick={() => { navigate('/dashboard'); setShowFloatingMenu(false); }}
                                                className={`flex-1 text-center text-xs font-bold py-1.5 rounded-lg transition-all ${isDashboard ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>App</button>
                                            <button onClick={() => { navigate('/'); setShowFloatingMenu(false); }}
                                                className={`flex-1 text-center text-xs font-bold py-1.5 rounded-lg transition-all ${!isDashboard ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>Site</button>
                                        </div>
                                        <button onClick={() => { navigate('/admin'); setShowFloatingMenu(false); }}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                            Admin
                                        </button>
                                        <button onClick={async () => { try { const svc = await getAdminService(); const r = await svc.togglePreview(); setPreviewMode(!!r.previewMode); window.location.reload(); } catch (e) { console.error('Toggle preview failed', e); } }}
                                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${previewMode ? 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-500/10' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                                            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                            {previewMode ? 'Preview Full Access ON' : 'Preview Full Access'}
                                        </button>
                                        <div className="px-3 py-2">
                                            {impersonating ? (
                                                <div className="flex items-center gap-2">
                                                    <span className="flex-1 text-xs text-amber-600 dark:text-amber-400 font-medium truncate">{impersonating.name || impersonating.email}</span>
                                                    <button onClick={stopImpersonation} className="text-[10px] font-bold text-red-500 hover:text-red-600 px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-500/10 transition">Stop</button>
                                                </div>
                                            ) : (
                                                <>
                                                    <input type="text" placeholder={language === 'fr' ? 'Simuler un user (#id / email)...' : 'Impersonate (#id / email)...'} value={impSearchQuery} onChange={e => handleImpSearch(e.target.value)}
                                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const q = impSearchQuery.trim(); if (q.startsWith('#')) { const id = parseInt(q.slice(1), 10); if (id > 0) startImpersonation(id); } else if (impSearchResults.length > 0) { startImpersonation(impSearchResults[0].id); } } }}
                                                        onClick={e => e.stopPropagation()}
                                                        className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-amber-500" />
                                                    {impSearching && <p className="text-[10px] text-gray-400 mt-1">Recherche...</p>}
                                                    {impSearchResults.length > 0 && (
                                                        <div className="mt-1 max-h-28 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                                                            {impSearchResults.map(u => (
                                                                <button key={u.id} onClick={() => startImpersonation(u.id)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-amber-50 dark:hover:bg-amber-500/10 transition">
                                                                    <span className="font-medium text-gray-900 dark:text-white">{u.name}</span>
                                                                    <span className="text-gray-500 dark:text-gray-400 ml-1">#{u.id}</span><br />
                                                                    <span className="text-gray-400 dark:text-gray-500">{u.email}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="border-t border-gray-100 dark:border-white/5" />
                                    </>
                                )}
                                {isSchoolAdmin && (
                                    <>
                                        <button onClick={() => { navigate('/dashboard/account/school-stats'); setShowFloatingMenu(false); }}
                                            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                            {language === 'fr' ? 'Stats école' : 'School stats'}
                                        </button>
                                        <div className="border-t border-gray-100 dark:border-white/5" />
                                    </>
                                )}
                                {/* Language & Theme */}
                                <div className="flex items-center justify-between px-4 py-2">
                                    <button
                                        onClick={toggleLanguage}
                                        className="px-2.5 py-1 rounded-md text-xs font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition"
                                    >
                                        {language === 'fr' ? 'EN' : 'FR'}
                                    </button>
                                    <button
                                        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                                        className="p-1.5 rounded-lg transition hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400"
                                    >
                                        {theme === 'dark' ? (
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                        ) : (
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                                        )}
                                    </button>
                                </div>
                                <div className="border-t border-gray-100 dark:border-white/5" />
                                <button onClick={() => { navigate('/dashboard/account'); setShowFloatingMenu(false); }}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                    {user?.dashboardPrefs?.avatarId ? (
                                        <UserAvatar size="xs" />
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                    )}
                                    {language === 'fr' ? 'Mon compte' : 'My account'}
                                </button>
                                <div className="border-t border-gray-100 dark:border-white/5" />
                                <button onClick={handleLogout}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                                    {language === 'fr' ? 'Déconnexion' : 'Sign out'}
                                </button>
                            </div>
                        )}
                        <button
                            onClick={() => setShowFloatingMenu(v => !v)}
                            className="hover:scale-105 transition-all shadow-lg hover:shadow-xl rounded-full"
                            title={user?.name || user?.email}
                        >
                            <UserAvatar size="md" showNotificationDot />
                        </button>
                    </div>
                </div>
            </div>
            )}

            {/* Mobile Horizontal Level Pills (hidden on content pages) */}
            {!isContentPage && (
            <div className="lg:hidden fixed top-16 left-0 right-0 bg-gray-50 dark:bg-[#0a0a0a] z-40 px-3 py-1.5 overflow-x-auto scrollbar-none border-b border-gray-200 dark:border-white/5 shadow-sm transition-colors duration-300">
                <div className="flex gap-2 items-center">
                    {visibleLevels.map((module) => {
                        const isActive = levelId === module.id;
                        return (
                            <button
                                key={module.id}
                                onClick={() => handleLevelClick(module.id)}
                                className={`flex-shrink-0 rounded-full px-4 h-10 flex items-center text-[10px] font-black transition-all ${
                                    isActive
                                        ? 'shadow-md scale-105 ring-2 ring-offset-1 ring-white/30 dark:ring-white/20 text-white'
                                        : 'bg-gray-200 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:opacity-100 opacity-70'
                                }`}
                                style={isActive ? { backgroundColor: module.newBackgroundColor } : {}}
                            >
                                {module.titleObj?.[language] || module.title}
                            </button>
                        );
                    })}
                    <button
                        onClick={() => setShowLevelModal(true)}
                        className="flex-shrink-0 w-14 h-14 rounded-2xl border-2 border-dashed border-gray-300 dark:border-white/10 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-white hover:border-blue-300 dark:hover:border-white/20 flex items-center justify-center transition-all"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                    </button>
                </div>
            </div>
            )}

            {/* Mobile Bottom Nav - Kept for other navigation */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 shadow-lg border-t border-gray-200 dark:border-gray-800 z-50 flex justify-around p-2 transition-colors duration-300 hidden">
                {visibleLevels.map((module) => (
                    <button
                        key={module.id}
                        onClick={() => handleLevelClick(module.id)}
                        style={{
                            backgroundColor: levelId === module.id ? module.newBackgroundColor : undefined,
                            color: levelId === module.id ? '#fff' : undefined
                        }}
                        className={`px-3 py-2 rounded-lg text-xs font-bold transition
                            ${levelId !== module.id ? 'text-gray-500 dark:text-gray-400' : ''}
                        `}
                    >
                        {module.titleObj?.[language] || module.title}
                    </button>
                ))}
            </div>

            {/* Main Content */}
            <div id="dashboard-scroll-container" className={`flex-1 overflow-y-auto overflow-x-hidden pb-20 lg:pb-0 custom-scrollbar ${isContentPage ? 'pt-0' : 'pt-20 lg:pt-0'}`}>
                <Outlet />
            </div>

            {/* Exam Session Modal Overlay */}
            {examId && (
                <div className="fixed inset-0 z-[200] bg-gray-50 dark:bg-black">
                    <ExamSession />
                </div>
            )}

            {/* Quiz Session Modal Overlay */}
            {quizId && (
                <div className="fixed inset-0 z-[200] bg-gray-50 dark:bg-black">
                    <QuizSession />
                </div>
            )}

            {/* Level Selector Modal */}
            {showLevelModal && (
                <LevelSelectorModal
                    prefs={prefs}
                    onSave={(newOrder, newAdded, newHidden) => {
                        setSidebarOrder(newOrder);
                        setAddedLevels(newAdded);
                        setHiddenLevels(newHidden);
                    }}
                    onClose={() => setShowLevelModal(false)}
                />
            )}

            {/* Contextual Help System */}
            <HelpSpotlight />
            <HelpTrigger />


            {/* ── Account modal ─────────────────────────────────────────────── */}
            {showAccountModal && (
                <UserAccountModal key={accountSection} defaultSection={accountSection} onClose={closeAccountModal} />
            )}
        </div>
    );
};

export default DashboardLayout;
